import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Siren, Plus, Pencil, Trash2, X, MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import type { EmergencyPlan, EmergencyPlanType, EmergencyPlanStatus, EmergencyContact, FarmOption } from '../../../types/whs';
import { Badge, EMERGENCY_TYPE_LABEL } from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz, getTodayInTz } from '../../../utils/timezone';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';
const labelCls = 'block text-xs font-medium text-neutral-600 mb-1';

const orNull = (v: string) => (v.trim() === '' ? null : v.trim());

const STATUS_LABEL: Record<EmergencyPlanStatus, string> = { draft: 'Draft', current: 'Current', archived: 'Archived' };
const STATUS_CLASS: Record<EmergencyPlanStatus, string> = {
  draft: 'bg-neutral-100 text-neutral-700',
  current: 'bg-emerald-100 text-emerald-800',
  archived: 'bg-neutral-200 text-neutral-500',
};

function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

interface PlanForm {
  title: string;
  plan_type: EmergencyPlanType;
  location: string;
  farm_id: string;
  assembly_point: string;
  procedures: string;
  last_drill_date: string;
  next_drill_date: string;
  review_date: string;
  status: EmergencyPlanStatus;
  emergency_contacts: EmergencyContact[];
  wardens: EmergencyContact[];
}

const emptyForm: PlanForm = {
  title: '', plan_type: 'evacuation', location: '', farm_id: '', assembly_point: '',
  procedures: '', last_drill_date: '', next_drill_date: '', review_date: '', status: 'draft',
  emergency_contacts: [], wardens: [],
};

function ContactRows({ rows, onChange }: { rows: EmergencyContact[]; onChange: (rows: EmergencyContact[]) => void }) {
  const update = (i: number, key: keyof EmergencyContact, value: string) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r));
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          <input className={inputCls} placeholder="Name" value={r.name ?? ''} onChange={(e) => update(i, 'name', e.target.value)} />
          <input className={inputCls} placeholder="Role" value={r.role ?? ''} onChange={(e) => update(i, 'role', e.target.value)} />
          <input className={inputCls} placeholder="Phone" value={r.phone ?? ''} onChange={(e) => update(i, 'phone', e.target.value)} />
          <button type="button" onClick={() => onChange(rows.filter((_, idx) => idx !== i))} className="shrink-0 rounded p-2 text-neutral-400 hover:bg-red-50 hover:text-red-600" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, { name: '', role: '', phone: '' }])} className="text-sm font-medium text-primary-600 hover:text-primary-700">+ Add row</button>
    </div>
  );
}

function EmergencyPlansInner() {
  const orgTz = useOrgTimezone();
  const today = getTodayInTz(orgTz);

  const [plans, setPlans] = useState<EmergencyPlan[]>([]);
  const [farms, setFarms] = useState<FarmOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EmergencyPlan | null>(null);
  const [form, setForm] = useState<PlanForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.listEmergencyPlans();
    if (res.success) { setPlans(res.data); setError(null); } else setError(res.error.message);
    setLoading(false);
  }, []);

  const loadFarms = useCallback(async () => {
    const res = await whsService.getReportOptions();
    if (res.success) setFarms(res.data.farms);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFarms(); }, [loadFarms]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (p: EmergencyPlan) => {
    setEditing(p);
    setForm({
      title: p.title,
      plan_type: p.plan_type,
      location: p.location ?? '',
      farm_id: p.farm_id != null ? String(p.farm_id) : '',
      assembly_point: p.assembly_point ?? '',
      procedures: p.procedures ?? '',
      last_drill_date: p.last_drill_date ?? '',
      next_drill_date: p.next_drill_date ?? '',
      review_date: p.review_date ?? '',
      status: p.status,
      emergency_contacts: p.emergency_contacts ?? [],
      wardens: p.wardens ?? [],
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    const payload: Partial<EmergencyPlan> = {
      title: form.title.trim(),
      plan_type: form.plan_type,
      location: orNull(form.location),
      farm_id: form.farm_id ? Number(form.farm_id) : null,
      assembly_point: orNull(form.assembly_point),
      procedures: orNull(form.procedures),
      last_drill_date: orNull(form.last_drill_date),
      next_drill_date: orNull(form.next_drill_date),
      review_date: orNull(form.review_date),
      status: form.status,
      emergency_contacts: form.emergency_contacts.filter((c) => (c.name || c.role || c.phone)),
      wardens: form.wardens.filter((c) => (c.name || c.role || c.phone)),
    };
    const res = editing
      ? await whsService.updateEmergencyPlan(editing.id, payload)
      : await whsService.createEmergencyPlan(payload);
    setSaving(false);
    if (res.success) { setModalOpen(false); setError(null); await load(); }
    else setError(res.error.message);
  };

  const remove = async (p: EmergencyPlan) => {
    if (!window.confirm(`Delete emergency plan "${p.title}"?`)) return;
    const res = await whsService.deleteEmergencyPlan(p.id);
    if (res.success) await load(); else setError(res.error.message);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Emergency Preparedness"
        subtitle="Evacuation & emergency plans, wardens, assembly points and drill scheduling"
        icon={<Siren className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        <div className="mb-6 flex justify-end">
          <Button onClick={openCreate}><Plus className="h-4 w-4" /> New plan</Button>
        </div>

        {loading ? (
          <p className="text-center text-neutral-400">Loading…</p>
        ) : plans.length === 0 ? (
          <p className="text-center text-neutral-400">No emergency plans yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {plans.map((p) => {
              const nextPast = !!p.next_drill_date && p.next_drill_date < today;
              const isOpen = expanded === p.id;
              return (
                <div key={p.id} className="rounded-xl border border-neutral-200 bg-white p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-neutral-900">{p.title}</h3>
                        <Badge label={STATUS_LABEL[p.status]} className={STATUS_CLASS[p.status]} />
                      </div>
                      <Badge label={EMERGENCY_TYPE_LABEL[p.plan_type]} className="mt-1 bg-orange-100 text-orange-800" />
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(p)} className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(p)} className="rounded p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>

                  <div className="mt-3 space-y-1 text-sm text-neutral-600">
                    {(p.location || p.farm_name) && (
                      <p className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-neutral-400" />{[p.farm_name, p.location].filter(Boolean).join(' — ')}</p>
                    )}
                    {p.assembly_point && <p><span className="font-medium text-neutral-700">Assembly:</span> {p.assembly_point}</p>}
                    <p>
                      <span className="font-medium text-neutral-700">Last drill:</span> {p.last_drill_date ? formatLocalDateInTz(p.last_drill_date, orgTz) : '—'}
                      <span className="mx-2 text-neutral-300">•</span>
                      <span className="font-medium text-neutral-700">Next drill:</span>{' '}
                      <span className={nextPast ? 'font-semibold text-red-600' : ''}>{p.next_drill_date ? formatLocalDateInTz(p.next_drill_date, orgTz) : '—'}</span>
                    </p>
                  </div>

                  {(p.procedures || p.emergency_contacts.length > 0 || p.wardens.length > 0) && (
                    <button onClick={() => setExpanded(isOpen ? null : p.id)} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {isOpen ? 'Hide details' : 'Show details'}
                    </button>
                  )}

                  {isOpen && (
                    <div className="mt-3 space-y-3 border-t border-neutral-100 pt-3 text-sm">
                      {p.procedures && (
                        <div>
                          <p className="font-medium text-neutral-700">Procedures</p>
                          <p className="whitespace-pre-wrap text-neutral-600">{p.procedures}</p>
                        </div>
                      )}
                      {p.emergency_contacts.length > 0 && (
                        <div>
                          <p className="font-medium text-neutral-700">Emergency contacts</p>
                          <ul className="text-neutral-600">
                            {p.emergency_contacts.map((c, i) => <li key={i}>{[c.name, c.role, c.phone].filter(Boolean).join(' — ')}</li>)}
                          </ul>
                        </div>
                      )}
                      {p.wardens.length > 0 && (
                        <div>
                          <p className="font-medium text-neutral-700">Wardens</p>
                          <ul className="text-neutral-600">
                            {p.wardens.map((c, i) => <li key={i}>{[c.name, c.role, c.phone].filter(Boolean).join(' — ')}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalOpen && (
        <Modal
          title={editing ? 'Edit emergency plan' : 'New emergency plan'}
          onClose={() => setModalOpen(false)}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={submit} isLoading={saving}>{editing ? 'Save changes' : 'Create'}</Button>
            </>
          )}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls}>Title *</label>
              <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Plan type</label>
              <select className={inputCls} value={form.plan_type} onChange={(e) => setForm({ ...form, plan_type: e.target.value as EmergencyPlanType })}>
                {(Object.entries(EMERGENCY_TYPE_LABEL) as [EmergencyPlanType, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as EmergencyPlanStatus })}>
                {(Object.entries(STATUS_LABEL) as [EmergencyPlanStatus, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Farm</label>
              <select className={inputCls} value={form.farm_id} onChange={(e) => setForm({ ...form, farm_id: e.target.value })}>
                <option value="">— None —</option>
                {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Location</label>
              <input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Assembly point</label>
              <textarea rows={2} className={inputCls} value={form.assembly_point} onChange={(e) => setForm({ ...form, assembly_point: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Procedures</label>
              <textarea rows={4} className={inputCls} value={form.procedures} onChange={(e) => setForm({ ...form, procedures: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Last drill date</label>
              <input type="date" className={inputCls} value={form.last_drill_date} onChange={(e) => setForm({ ...form, last_drill_date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Next drill date</label>
              <input type="date" className={inputCls} value={form.next_drill_date} onChange={(e) => setForm({ ...form, next_drill_date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Review date</label>
              <input type="date" className={inputCls} value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Emergency contacts</label>
              <ContactRows rows={form.emergency_contacts} onChange={(rows) => setForm({ ...form, emergency_contacts: rows })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Wardens</label>
              <ContactRows rows={form.wardens} onChange={(rows) => setForm({ ...form, wardens: rows })} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function WhsEmergencyPlans() {
  return <WhsGate><EmergencyPlansInner /></WhsGate>;
}
